import { Arr } from '@deqo/foundation';
import { computePosition, offset } from '@floating-ui/react-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce, usePromise, useWindowSize } from 'react-use';
import styled, { keyframes } from 'styled-components';
import { Images } from '../../../@resources/img';
import { shuffle } from '../../../CodesForFoundation';
import { useObserver, uuid, waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Point } from '../../../platform/geom/Point';
import { useResolve } from '../../Container';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { Colors, remInPx } from './../../../ui';
import { withXmas } from './WithXmas';

// #xmasToolbarBracket {

// }

// #toolBarFirst > .t-float {
//     z-index: 1;
// }

const swingAnim = keyframes`
    8%, 41% {
        transform: translate(-50%, 0.4rem) rotate(-10deg);
    }
    25%, 58% {
        transform: translate(-50%, 0.4rem) rotate(10deg);
    }
    75% {
        transform: translate(-50%, 0.4rem) rotate(-5deg);
    }
    92% {
        transform: translate(-50%, 0.4rem) rotate(5deg);
    }
    0%, 100% {
        transform: translate(-50%, 0.4rem) rotate(0);
    }
`;

// @keyframes swingAnim {

// }

// #xmasToolbarBells {
//     position: relative;
//     width: 5.8rem;
//     top: -2.5rem;
//     transform-origin: 50% 1.0rem;
// }

// #xmasToolbarHolly {
//     position: relative;
//     width: 5.8rem;
//     top: -2.7rem;
//     left: -.2rem;
//     transform-origin: 50% 1.0rem;
// }

// #xmasToolbarWreathe {
//     position: relative;
//     width: 5.8rem;
//     top: -2.9rem;
//     transform-origin: 50% 1.0rem;
// }

// #xmasToolbarBells, #xmasToolbarHolly, #xmasToolbarWreathe {
//     &.--swing {
//         animation: swingAnim .75s linear;
//     }
// }

const Frame = styled.div`
    position: absolute;
    margin-top: -.875rem;
    user-select: none;
`;

const Bracket = styled.div`
    position: absolute;
    z-index: 0;
    top: -1rem;
    left: 0rem;
    width: 1rem;
    height: 1.5rem;
    border: solid 3px ${Colors.gray400};
    border-radius: 1.5rem;
    pointer-events: none;
    transform: translate(-50%, 0);
`;

const Hanger = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 3rem;
    height: 3rem;
    transform-origin: 50% 0;
    transform: translate(-50%, 0.4rem) rotate(0deg);

    &.--swing {
        animation: ${swingAnim} .75s linear;
    }

    img {
        position: relative;
        width: 3rem;
        height: 3rem;
        pointer-events: none;
    }
`;

const data = {
    [Images.XmasBells]: [0, -.5],
    [Images.XmasBow]: [0, -.5],
    [Images.XmasHolly]: [-.1, -.5],
    [Images.XmasOrnament1]: [0, -.3],
    [Images.XmasOrnament2]: [0, -.3],
    [Images.XmasOrnament3]: [0, -.3],
    [Images.XmasOrnament4]: [0, -.3],
    [Images.XmasOrnament5]: [0, -.3],
    [Images.XmasSock]: [-.2, -.2],
    [Images.XmasStar2]: [0, -.2],
    [Images.XmasWreath]: [0, 0],
};

const next = (() => {
    let i = 0;
    const ks = shuffle(Object.keys(data));
    return () => Arr.at(ks, i++);
})();

const swing = (e: any) => {
    const t = e.target as HTMLElement;
    if (t.classList.contains('--swing')) return;
    t.classList.add('--swing');
    setTimeout(() => t.classList.remove('--swing'), 800);
};

const useToolboxElement = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const widgets = useObserver(useResolve(WidgetManager));
    const elmt = document.getElementById('wc-toolbox');
    const k1 = JSON.stringify(elmt?.getBoundingClientRect() ?? {});
    const k2 = useWindowSize();
    return [elmt, useMemo(() => uuid(), [k1, k2])] as const;
};

const RenderDecal = () => {

    const [toolbox, key] = useToolboxElement();
    const floating = useRef<HTMLDivElement>();
    const mounted = usePromise();
    const [position, setPosition] = useState(null as Point);

    useEffect(() => {
        if (!floating.current) return;
        if (!toolbox) return;
        const opts: any = {
            placement: 'bottom',
            middleware: [offset(remInPx(1))]
        };

        mounted(computePosition(toolbox, floating.current, opts))
            .then(x => {
                setPosition(Point.from(x as any));
            });

    }, [floating.current, key]);

    const src = useMemo(next, []);
    const xy = data[src];
    const styling = {
        left: `${xy[0]}rem`,
        top: `${xy[1]}rem`,
    };

    if (!toolbox || !toolbox.clientHeight) return null;

    return <Frame ref={floating} style={{ left: position?.x, top: position?.y }}>
        <Bracket />
        <Hanger onClick={swing} >
            <img src={src} style={styling} />
        </Hanger>
    </Frame>;
};

const WhenReady: LFC = ({ children }) => {
    const [ready, setReady] = useState(false);
    useEffectOnce(() => void (waitFor(100).then(() => setReady(true))));
    return ready ? <>{children}</> : null;
};

export const XmasHangingDecal = withXmas(() => {
    return <WhenReady>
        <RenderDecal />
    </WhenReady>;
});
